import { Injectable } from '@angular/core';
import { Transport } from '../models/transport.model';
import { EstimateService } from './estimate.service';
import { PATHS } from '../models/bary-paths.model';
import { EstimationStatus, LastEstimate } from '../models/estimate-result-bundle.model';
import { ActivatedRoute, Router } from '@angular/router';
import { SubscribedContainer } from '../app/shared/util/subscribed-container';
import { AbstractControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AccountService } from './account.service';
import { EventManager } from './event-manager.service';
import { QuotationService } from './quotation.service';
import { BasketService } from './basket.service';
import { BaryValidator } from '../validators/bary.validator';
import { CurrentDataService } from './current-data-service';
import { BaryFormFactoryService } from './bary-form-factory.service';
import { BaryModalService } from './bary-modal.service';
import { TransportStep } from '../models/enums/transport-step.enum';
import { AddToBasket } from '../models/add-to-basket.model';
import { LocalStorageService } from './local-storage.service';
import { TransportFormGroup } from '../models/forms/TransportFormGroup';

@Injectable({
  providedIn: 'root',
})
export class EstimationTunnelService extends SubscribedContainer {
  constructor(
    public currentDataService: CurrentDataService,
    public estimateService: EstimateService,
    protected bcf: BaryFormFactoryService,
    protected eventManager: EventManager,
    protected router: Router,
    protected route: ActivatedRoute,
    protected translateService: TranslateService,
    protected modalService: BaryModalService,
    protected accountService: AccountService,
    protected basketService: BasketService,
    protected quotationService: QuotationService,
    protected localStorageService: LocalStorageService
  ) {
    super();
  }

  private static checkStep(stepIndex: number, controls: AbstractControl[], stepInErrors: any[]): void {
    const invalids = controls.filter(it => it.invalid);
    if (invalids.length > 0) {
      invalids.forEach(it => it.markAllAsTouched());
      stepInErrors.push(stepIndex);
    }
  }

  scrollToTop(): void {
    const scrollTop = (): void => {
      const c = document.documentElement.scrollTop || document.body.scrollTop;
      if (c > 0) {
        // window.requestAnimationFrame(scrollTop);
        window.scrollTo(0, 0);
      }
    };
    scrollTop();
  }

  /**
   * Mise à jour du tarif :
   * On met à jour le tarif en faisant un appel serveur.
   * Puis on met à jour le transport avec les données de retour.
   */
  refreshPrice(tempTransport: Transport, customerId: number | undefined): Observable<LastEstimate> {
    return this.estimateService.refreshPricing(tempTransport, customerId);
  }

  /**
   * Valide la step en filtrant les erreurs des autres steps.
   * @param tempTransport
   * @param step
   */
  validateStep(tempTransport: Transport, step: TransportStep): Observable<LastEstimate> {
    return this.estimateService.validateStep(tempTransport).pipe(
      map(lastEstimate => {
        lastEstimate.errorMessages = lastEstimate.errorMessages.filter(it => it.startsWith(step));
        if (lastEstimate.status === EstimationStatus.ERROR && lastEstimate.errorMessages.length === 0) {
          lastEstimate.status = EstimationStatus.OK;
        }
        return lastEstimate;
      })
    );
  }

  doAddToBasket(validatedTransport: Transport): Observable<AddToBasket> {
    return this.basketService.addTransportToBasket(validatedTransport).pipe(take(1));
  }

  doUpdateTransportInBasket(validatedTransport: Transport): Observable<AddToBasket> {
    return this.basketService.updateTransportInBasket(validatedTransport).pipe(take(1));
  }

  doCancel(): void {
    this.modalService
      .openConfirm(this.translateService.instant('estimation-tunnel.cancelMessage'))
      .action()
      .subscribe((action: any) => {
        if (action.submit) {
          this.currentDataService.removeCurrentTransport();
          this.currentDataService.removeTransportInModification();
          this.localStorageService.clearTransportForAccountCreation();
          if (this.accountService.isAuthenticated()) {
            this.router.navigateByUrl(PATHS.ORDER_BASKET.path);
          } else {
            this.router.navigateByUrl(PATHS.HOME.path);
          }
        }
      });
  }

  precheckValidateStep(stepNumber: number, transportForm: TransportFormGroup): number[] {
    const stepPickupValidator: AbstractControl[] = [];
    const stepDeliveryValidator: AbstractControl[] = [];
    const stepItemValidator: AbstractControl[] = [];
    const stepTransportExtraConditionsValidator: AbstractControl[] = [];
    if (stepNumber >= 0) {
      if (!transportForm.controls.pickupMeeting.value) {
        stepPickupValidator.push(transportForm.controls.pickupMeeting);
        stepPickupValidator.push(transportForm.controls.pickupDate);
        stepPickupValidator.push(transportForm.controls.pickupTimeslotStart);
        stepPickupValidator.push(transportForm.controls.pickupTimeslotEnd);
      }
      stepPickupValidator.push(transportForm.controls.pickupAddress.getAngularForm());
      stepPickupValidator.push(transportForm.controls.pickupContact.getAngularForm());
      stepPickupValidator.push(transportForm.controls.pickupAccessConstraint);
      stepPickupValidator.push(transportForm.controls.pickupLoadingConstraint);
    }
    if (stepNumber >= 1) {
      if (!transportForm.controls.deliveryMeeting.value) {
        stepDeliveryValidator.push(transportForm.controls.deliveryMeeting);
        stepDeliveryValidator.push(transportForm.controls.deliveryDate);
        stepDeliveryValidator.push(transportForm.controls.deliveryTimeslotStart);
        stepDeliveryValidator.push(transportForm.controls.deliveryTimeslotEnd);
      }
      stepDeliveryValidator.push(transportForm.controls.deliveryAddress.getAngularForm());
      stepDeliveryValidator.push(transportForm.controls.deliveryContact.getAngularForm());
      stepDeliveryValidator.push(transportForm.controls.deliveryAccessConstraint);
      stepDeliveryValidator.push(transportForm.controls.deliveryLoadingConstraint);
    }
    if (stepNumber >= 2) {
      stepItemValidator.push(transportForm.controls.itemsNature);
      stepItemValidator.push(transportForm.controls.euroPallet);
      stepItemValidator.push(transportForm.controls.goodsValue);
    }

    if (stepNumber >= 3) {
      // control temperature dirigee:
      if (transportForm.controls.isControlledTemperature.value) {
        if (!transportForm.controls.transportExtraConditions[0].controls.controlledTemperature.value) {
          const error = BaryValidator.createError('key', 'message');
          transportForm.controls.transportExtraConditions[0].controls.controlledTemperature.setErrors(error);
        }
        stepTransportExtraConditionsValidator.push(transportForm.controls.transportExtraConditions[0].controls.controlledTemperature);
      }
      if (transportForm.controls.isHazardousMaterial.value) {
        transportForm.controls.transportExtraConditions.forEach((transportExtraCondition, id) => {
          stepTransportExtraConditionsValidator.push(transportForm.controls.transportExtraConditions[id].controls.adrCodeUN);
          stepTransportExtraConditionsValidator.push(transportForm.controls.transportExtraConditions[id].controls.adrDescription);
          stepTransportExtraConditionsValidator.push(transportForm.controls.transportExtraConditions[id].controls.adrNetWeight);
        });

        stepTransportExtraConditionsValidator.push(transportForm.getAngularForm().controls['adrConfirm']);
      }
    }

    const stepsWithErrors: number[] = [];
    EstimationTunnelService.checkStep(0, stepPickupValidator, stepsWithErrors);
    EstimationTunnelService.checkStep(1, stepDeliveryValidator, stepsWithErrors);
    EstimationTunnelService.checkStep(2, stepItemValidator, stepsWithErrors);
    EstimationTunnelService.checkStep(3, stepTransportExtraConditionsValidator, stepsWithErrors);

    return stepsWithErrors;
  }

  public doQuotationRequest(validatedTransport: Transport): Observable<AddToBasket> {
    return this.quotationService.requestQuotation(validatedTransport).pipe(take(1));
  }

  saveInLocalStorage(validatedTransport: Transport): void {
    this.localStorageService.saveTransportForAccountCreation(validatedTransport);
    this.currentDataService.updateTransportInModification(validatedTransport);
  }
}
